// Testimonials.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiVideo,
  FiImage,
  FiFileText,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiUser,
  FiLink,
  FiPlay,
  FiUpload,
  FiX,
  FiFilm,
  FiCamera,
} from "react-icons/fi";
import { FaRegSmile, FaVideo, FaImage, FaQuoteLeft } from "react-icons/fa";

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [viewingTestimonial, setViewingTestimonial] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "cr_date",
    direction: "desc",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    ttype: "",
    ttitle: "",
    tsub: "",
    tdet: "",
    tvlink: "",
    timg: null,
    is_live: 0,
  });

  const API_BASE = "https://dev.mithahomes.com/backend/api/testimonials";
  const IMaeURl = "https://dev.mithahomes.com/backend";

  // Statistics
  const stats = {
    total: testimonials.length,
    live: testimonials.filter((t) => t.is_live).length,
    video: testimonials.filter((t) => t.ttype === "video").length,
    draft: testimonials.filter((t) => !t.is_live).length,
  };

  // Fetch all testimonials
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/all`);
      if (response.data.success) {
        setTestimonials(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("❌ Error loading testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Filter testimonials
  const filteredTestimonials = testimonials.filter((testimonial) => {
    const matchesSearch =
      testimonial.ttitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testimonial.tsub?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testimonial.tdet?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      filterType === "all" || testimonial.ttype === filterType;
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "live" && testimonial.is_live) ||
      (filterStatus === "draft" && !testimonial.is_live);
    return matchesSearch && matchesType && matchesStatus;
  });

  // Sort testimonials
  const sortedTestimonials = React.useMemo(() => {
    if (!sortConfig.key) return filteredTestimonials;

    return [...filteredTestimonials].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredTestimonials, sortConfig]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear media previews when type changes
    if (name === "ttype" && value !== formData.ttype) {
      setImagePreview(null);
      setVideoPreview(null);
      setFormData((prev) => ({ ...prev, timg: null, tvlink: "" }));
    }
  };

  // Handle file upload for both images and videos
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type based on testimonial type
    if (formData.ttype === "image") {
      if (!file.type.startsWith("image/")) {
        toast.error("❌ Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("❌ Image size should be less than 5MB");
        return;
      }
    } else if (formData.ttype === "video") {
      if (!file.type.startsWith("video/")) {
        toast.error("❌ Please select a valid video file");
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        // 50MB limit
        toast.error("❌ Video size should be less than 50MB");
        return;
      }
    }

    setFormData((prev) => ({ ...prev, timg: file }));

    // Create preview
    if (formData.ttype === "image") {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setVideoPreview(null);
    } else if (formData.ttype === "video") {
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
      setImagePreview(null);
    }
  };

  // Handle video link input
  const handleVideoLinkChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, tvlink: value }));

    // Clear uploaded file when video link is provided
    if (value) {
      setFormData((prev) => ({ ...prev, timg: null }));
      setVideoPreview(null);
      setImagePreview(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      ttype: "",
      ttitle: "",
      tsub: "",
      tdet: "",
      tvlink: "",
      timg: null,
      is_live: 0,
    });
    setEditingTestimonial(null);
    setImagePreview(null);
    setVideoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove uploaded media
  const removeMedia = () => {
    setFormData((prev) => ({ ...prev, timg: null, tvlink: "" }));
    setImagePreview(null);
    setVideoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.ttype) {
      toast.error("❌ Please select a testimonial type");
      return false;
    }
    if (!formData.ttitle.trim()) {
      toast.error("❌ Please enter a title");
      return false;
    }

    // Validate media based on type
    if (formData.ttype === "video") {
      if (
        !formData.tvlink &&
        !formData.timg &&
        !editingTestimonial?.timg &&
        !editingTestimonial?.tvlink
      ) {
        toast.error(
          "❌ Please provide either a video link or upload a video file",
        );
        return false;
      }
    } else if (formData.ttype === "image") {
      if (!formData.timg && !editingTestimonial?.timg) {
        toast.error("❌ Please upload an image file");
        return false;
      }
    }

    return true;
  };

  // ✅ Add new testimonial or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setUploading(true);
      const submitData = new FormData();

      // Append all form data
      submitData.append("ttype", formData.ttype);
      submitData.append("ttitle", formData.ttitle);
      submitData.append("tsub", formData.tsub);
      submitData.append("tdet", formData.tdet);
      submitData.append("tvlink", formData.tvlink);
      submitData.append("is_live", formData.is_live);

      // Append file if exists (for both image and video uploads)
      if (formData.timg) {
        submitData.append("timg", formData.timg);
      }

      let response;
      if (editingTestimonial) {
        // Update testimonial

        // Note: axios.put with FormData and file upload often requires an API that handles it.
        // If the API only supports form-data for POST, a common workaround for PUT is to
        // submit the file separately or use a POST method override.
        // Assuming the API supports multipart/form-data for PUT:
        response = await axios.post(
          `${API_BASE}/update/${editingTestimonial.tid}`,
          submitData,
          {
            headers: {
              // Often, setting Content-Type to multipart/form-data with axios.post for update
              // and letting the browser handle the boundary is the most reliable way for files.
              "Content-Type": "multipart/form-data",
              // If the PUT method is strictly required by the API, you may need a method override.
              // submitData.append('_method', 'PUT'); // If the backend uses this convention
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`Upload Progress: ${progress}%`);
            },
          },
        );
      } else {
        // Create new testimonial
        response = await axios.post(`${API_BASE}/add`, submitData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            console.log(`Upload Progress: ${progress}%`);
          },
        });
      }

      if (response.data.success) {
        toast.success(
          `✅ ${editingTestimonial ? "Testimonial updated" : "Testimonial added"} successfully!`,
        );
        setShowModal(false);
        resetForm();
        fetchTestimonials();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving testimonial:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving testimonial";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  // ✅ Edit testimonial
  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      ttype: testimonial.ttype || "",
      ttitle: testimonial.ttitle || "",
      tsub: testimonial.tsub || "",
      tdet: testimonial.tdet || "",
      tvlink: testimonial.tvlink || "",
      timg: null, // Clear file input for security/re-upload logic
      is_live: testimonial.is_live || 0,
    });

    // Set appropriate preview based on type and existing media path
    if (testimonial.ttype === "image" && testimonial.timg) {
      // Assuming timg holds the path/URL for existing media
      setImagePreview(testimonial.timg);
      setVideoPreview(null);
    } else if (testimonial.ttype === "video" && testimonial.timg) {
      setVideoPreview(testimonial.timg); // Use existing video path for preview
      setImagePreview(null);
    } else if (testimonial.ttype === "video" && testimonial.tvlink) {
      setVideoPreview(null);
      setImagePreview(null);
    }

    setShowModal(true);
  };

  // View testimonial details
  const handleView = (testimonial) => {
    setViewingTestimonial(testimonial);
    setShowViewModal(true);
  };

  // ✅ Toggle live status
  const toggleLiveStatus = async (id, currentStatus) => {
    try {
      // Use POST for status update for broader API compatibility
      const response = await axios.post(`${API_BASE}/update/${id}`, {
        is_live: currentStatus ? 0 : 1,
      });

      if (response.data.success) {
        toast.success(
          currentStatus
            ? "📝 Testimonial moved to draft"
            : "🚀 Testimonial published!",
        );
        fetchTestimonials();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating status";
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // ✅ Delete testimonial
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this testimonial? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(`${API_BASE}/delete/${id}`);

        if (response.data.success) {
          toast.success("🗑️ Testimonial deleted successfully!");
          fetchTestimonials();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting testimonial:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting testimonial";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case "video":
        return <FaVideo style={{ color: "#ef4444", fontSize: "1.2rem" }} />;
      case "image":
        return <FaImage style={{ color: "#10b981", fontSize: "1.2rem" }} />;
      case "text":
        return <FaQuoteLeft style={{ color: "#667eea", fontSize: "1.2rem" }} />;
      default:
        return <FaRegSmile style={{ color: "#6b7280", fontSize: "1.2rem" }} />;
    }
  };

  // Get media preview for table view
  const getMediaPreview = (testimonial) => {
    const mediaPath = testimonial.timg;

    if (testimonial.ttype === "video") {
      if (testimonial.tvlink) {
        // YouTube thumbnail extraction
        const youtubeMatch = testimonial.tvlink.match(
          /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
        );
        if (youtubeMatch) {
          return `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`;
        }
      } else if (mediaPath) {
        // Uploaded video - use the path, the table will show a placeholder if not an image
        return mediaPath;
      }
    } else if (testimonial.ttype === "image" && mediaPath) {
      return mediaPath;
    }
    return null;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  // Get status color
  const getStatusColor = (isLive) => {
    return isLive
      ? { background: "#d1fae5", color: "#065f46", border: "#a7f3d0" }
      : { background: "#fef3c7", color: "#92400e", border: "#fde68a" };
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case "video":
        return { background: "#fee2e2", color: "#dc2626", border: "#fecaca" };
      case "image":
        return { background: "#d1fae5", color: "#059669", border: "#a7f3d0" };
      case "text":
        return { background: "#e0e7ff", color: "#3730a3", border: "#c7d2fe" };
      default:
        return { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" };
    }
  };

  // Render media upload section based on selected type
  const renderMediaUploadSection = () => {
    const isFileSelected = formData.timg;
    const isExistingImage =
      editingTestimonial?.ttype === "image" &&
      editingTestimonial?.timg &&
      !isFileSelected;
    const isExistingVideoFile =
      editingTestimonial?.ttype === "video" &&
      editingTestimonial?.timg &&
      !isFileSelected;

    switch (formData.ttype) {
      case "image":
        return (
          <div style={{ marginBottom: "25px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                color: "#374151",
                fontSize: "0.95rem",
              }}
            >
              📸 Image Upload *
            </label>
            <div
              style={{
                border: "2px dashed #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                transition: "all 0.3s ease",
                background:
                  imagePreview || isExistingImage ? "transparent" : "#f9f9f9",
              }}
            >
              {imagePreview || isExistingImage ? (
                <div>
                  <img
                    src={
                      imagePreview && imagePreview.startsWith("data:")
                        ? imagePreview
                        : IMaeURl + imagePreview
                    }
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      borderRadius: "10px",
                      marginBottom: "15px",
                      border: "2px solid #e2e8f0",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={removeMedia}
                      style={{
                        background: "#ef4444",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <FiX /> Remove Image
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <FiCamera
                    style={{
                      fontSize: "3rem",
                      color: "#cbd5e1",
                      marginBottom: "15px",
                    }}
                  />
                  <p
                    style={{
                      margin: "0 0 15px 0",
                      color: "#666",
                      fontSize: "1rem",
                    }}
                  >
                    Drag & drop or click to upload image
                  </p>
                  <p
                    style={{
                      margin: "0 0 15px 0",
                      color: "#94a3b8",
                      fontSize: "0.9rem",
                    }}
                  >
                    Supports: JPG, PNG, GIF • Max: 5MB
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case "video":
        return (
          <>
            {/* Video Link Input */}
            <div style={{ marginBottom: "25px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#374151",
                  fontSize: "0.95rem",
                }}
              >
                🎥 Video Link (YouTube/Vimeo)
              </label>
              <input
                type="url"
                name="tvlink"
                value={formData.tvlink}
                onChange={handleVideoLinkChange}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#667eea";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(102, 126, 234, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: "#94a3b8",
                  fontSize: "0.85rem",
                }}
              >
                Or upload a video file below
              </p>
            </div>

            {/* Video File Upload */}
            <div style={{ marginBottom: "25px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#374151",
                  fontSize: "0.95rem",
                }}
              >
                🎬 Video File Upload
              </label>
              <div
                style={{
                  border: "2px dashed #e2e8f0",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  background:
                    videoPreview || isExistingVideoFile
                      ? "transparent"
                      : "#f9f9f9",
                }}
              >
                {(videoPreview || isExistingVideoFile) && !formData.tvlink ? (
                  <div>
                    <video
                      src={
                        videoPreview && videoPreview.startsWith("blob:")
                          ? videoPreview
                          : IMaeURl + videoPreview
                      }
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "10px",
                        marginBottom: "15px",
                        border: "2px solid #e2e8f0",
                      }}
                      controls
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        onClick={removeMedia}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FiX /> Remove Video
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <FiFilm
                      style={{
                        fontSize: "3rem",
                        color: "#cbd5e1",
                        marginBottom: "15px",
                      }}
                    />
                    <p
                      style={{
                        margin: "0 0 15px 0",
                        color: "#666",
                        fontSize: "1rem",
                      }}
                    >
                      Drag & drop or click to upload video
                    </p>
                    <p
                      style={{
                        margin: "0 0 15px 0",
                        color: "#94a3b8",
                        fontSize: "0.9rem",
                      }}
                    >
                      Supports: MP4, MOV, AVI • Max: 50MB
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleFileChange}
                      disabled={!!formData.tvlink} // Disable if link is entered
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        cursor: !!formData.tvlink ? "not-allowed" : "pointer",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );

      case "text":
        return (
          <div style={{ marginBottom: "25px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                color: "#374151",
                fontSize: "0.95rem",
              }}
            >
              📝 Text Testimonial
            </label>
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                textAlign: "center",
                color: "#64748b",
              }}
            >
              <FiFileText style={{ fontSize: "2rem", marginBottom: "10px" }} />
              <p style={{ margin: "0", fontSize: "0.9rem" }}>
                Text testimonials don't require media upload. Focus on writing
                compelling content.
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div
            style={{
              background: "#f8fafc",
              padding: "30px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <FaRegSmile style={{ fontSize: "2.5rem", marginBottom: "15px" }} />
            <p style={{ margin: "0", fontSize: "1rem", fontWeight: "500" }}>
              Please select a testimonial type to see upload options
            </p>
          </div>
        );
    }
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Testimonials Management
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Manage customer testimonials and reviews
          </p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            resetForm();
          }}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
          }}
        >
          <FiPlus style={{ fontSize: "1.1rem" }} />
          Add New Testimonial
        </button>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Testimonials */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FaRegSmile />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.total}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Total Testimonials
            </p>
          </div>
        </div>

        {/* Live Testimonials */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiEye />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.live}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Live Testimonials
            </p>
          </div>
        </div>

        {/* Video Testimonials */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiVideo />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.video}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Video Testimonials
            </p>
          </div>
        </div>

        {/* Draft Testimonials */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiEyeOff />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.draft}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Draft Testimonials
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar (Completion) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {/* Search Input */}
        <div style={{ position: "relative", flexGrow: 1, maxWidth: "400px" }}>
          <input
            type="text"
            placeholder="Search by title, subject, or detail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px 12px 40px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              outline: "none",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
            }}
          />
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
            }}
          />
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
          {/* Type Filter */}
          <div style={{ position: "relative" }}>
            <FiFilter
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
              }}
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                appearance: "none",
                padding: "12px 16px 12px 40px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                fontSize: "1rem",
                cursor: "pointer",
                minWidth: "150px",
                color: "#374151",
              }}
            >
              <option value="all">All Types</option>
              <option value="video">Video</option>
              <option value="image">Image</option>
              <option value="text">Text</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#94a3b8",
              }}
            >
              <FiArrowDown size={18} />
            </div>
          </div>

          {/* Status Filter */}
          <div style={{ position: "relative" }}>
            <FiEyeOff
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
              }}
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                appearance: "none",
                padding: "12px 16px 12px 40px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                fontSize: "1rem",
                cursor: "pointer",
                minWidth: "150px",
                color: "#374151",
              }}
            >
              <option value="all">All Statuses</option>
              <option value="live">Live</option>
              <option value="draft">Draft</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#94a3b8",
              }}
            >
              <FiArrowDown size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          overflowX: "auto",
        }}
      >
        {loading ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#64748b" }}
          >
            <FiUpload
              style={{ fontSize: "2rem", animation: "spin 1s linear infinite" }}
            />
            <p>Loading testimonials...</p>
            <style>{`
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
          </div>
        ) : sortedTestimonials.length === 0 ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#64748b" }}
          >
            <FiFileText style={{ fontSize: "2rem", marginBottom: "15px" }} />
            <p style={{ fontSize: "1.1rem", fontWeight: "500" }}>
              No testimonials found.
            </p>
            <p style={{ margin: "0", fontSize: "0.9rem" }}>
              Try adjusting your search and filter settings, or click 'Add New
              Testimonial' to create one.
            </p>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 10px",
              padding: "0 10px",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8fafc",
                  fontWeight: "600",
                  color: "#64748b",
                  textAlign: "left",
                }}
              >
                {[
                  "Media",
                  "Type",
                  "Title",
                  "Subject",
                  "Date",
                  "Status",
                  "Actions",
                ].map((header, index) => {
                  const sortKey = [
                    "timg",
                    "ttype",
                    "ttitle",
                    "tsub",
                    "cr_date",
                    "is_live",
                    "",
                  ][index];
                  const isSortable = !!sortKey;
                  const isCurrentSort = sortConfig.key === sortKey;
                  const directionIcon = isCurrentSort ? (
                    sortConfig.direction === "asc" ? (
                      <FiArrowUp />
                    ) : (
                      <FiArrowDown />
                    )
                  ) : (
                    <FiArrowDown style={{ opacity: 0 }} />
                  );

                  const headerStyle = {
                    padding: "15px 20px",
                    cursor: isSortable ? "pointer" : "default",
                    borderBottom: "2px solid #e2e8f0",
                    whiteSpace: "nowrap",
                  };

                  return (
                    <th
                      key={header}
                      style={headerStyle}
                      onClick={
                        isSortable ? () => handleSort(sortKey) : undefined
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {header}
                        {isSortable && directionIcon}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedTestimonials.map((t) => {
                const statusStyle = getStatusColor(t.is_live);
                const typeStyle = getTypeColor(t.ttype);
                const mediaPreview = getMediaPreview(t);

                return (
                  <tr
                    key={t.tid}
                    style={{
                      background: "white",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      transition: "transform 0.2s ease",
                      border: "1px solid #e2e8f0",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "scale(1.005)";
                      e.currentTarget.style.boxShadow =
                        "0 5px 15px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 1px 3px rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                        width: "80px",
                      }}
                    >
                      <div
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          background: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {mediaPreview ? (
                          t.ttype === "video" && !t.tvlink ? (
                            <FiPlay
                              style={{ fontSize: "1.5rem", color: "#64748b" }}
                            />
                          ) : (
                            <img
                              src={
                                t.ttype === "video" && t.tvlink
                                  ? mediaPreview
                                  : IMaeURl + mediaPreview
                              }
                              alt="Media Preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          )
                        ) : (
                          getTypeIcon(t.ttype)
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <span
                        style={{
                          background: typeStyle.background,
                          color: typeStyle.color,
                          padding: "5px 10px",
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          border: `1px solid ${typeStyle.border}`,
                        }}
                      >
                        {t.ttype || "N/A"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {t.ttitle}
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                        color: "#64748b",
                      }}
                    >
                      {t.tsub || "—"}
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FiCalendar style={{ color: "#94a3b8" }} />
                        {formatDate(t.cr_date)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <span
                        style={{
                          background: statusStyle.background,
                          color: statusStyle.color,
                          padding: "5px 10px",
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          border: `1px solid ${statusStyle.border}`,
                        }}
                      >
                        {t.is_live ? "Live" : "Draft"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "15px 20px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "nowrap",
                        }}
                      >
                        {/* View Button */}
                        <button
                          onClick={() => handleView(t)}
                          title="View Details"
                          style={{
                            background: "#eff6ff",
                            color: "#2563eb",
                            border: "1px solid #bfdbfe",
                            padding: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#dbeafe";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#eff6ff";
                          }}
                        >
                          <FiEye />
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => handleEdit(t)}
                          title="Edit Testimonial"
                          style={{
                            background: "#fef3c7",
                            color: "#d97706",
                            border: "1px solid #fde68a",
                            padding: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#fcd34d";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#fef3c7";
                          }}
                        >
                          <FiEdit />
                        </button>

                        {/* Toggle Live Status Button */}
                        <button
                          onClick={() => toggleLiveStatus(t.tid, t.is_live)}
                          title={t.is_live ? "Move to Draft" : "Publish Live"}
                          style={{
                            background: t.is_live ? "#fee2e2" : "#d1fae5",
                            color: t.is_live ? "#dc2626" : "#059669",
                            border: t.is_live
                              ? "1px solid #fecaca"
                              : "1px solid #a7f3d0",
                            padding: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.opacity = "0.8";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.opacity = "1";
                          }}
                        >
                          {t.is_live ? <FiEyeOff /> : <FiEye />}
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(t.tid)}
                          title="Delete Testimonial"
                          style={{
                            background: "#fef2f2",
                            color: "#ef4444",
                            border: "1px solid #fecaca",
                            padding: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#fca5a5";
                            e.target.style.color = "white";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#fef2f2";
                            e.target.style.color = "#ef4444";
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Testimonial Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "700px",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "30px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
              }}
            >
              <h2 style={{ fontSize: "1.8rem", color: "#1e293b", margin: "0" }}>
                {editingTestimonial
                  ? "Edit Testimonial"
                  : "Add New Testimonial"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#64748b",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.color = "#ef4444";
                }}
                onMouseOut={(e) => {
                  e.target.style.color = "#64748b";
                }}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Type Select */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Type *
                </label>
                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  {["text", "image", "video"].map((type) => (
                    <label
                      key={type}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 15px",
                        border:
                          formData.ttype === type
                            ? "2px solid #667eea"
                            : "2px solid #e2e8f0",
                        borderRadius: "8px",
                        background:
                          formData.ttype === type ? "#eef2ff" : "white",
                        transition: "all 0.2s ease",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      <input
                        type="radio"
                        name="ttype"
                        value={type}
                        checked={formData.ttype === type}
                        onChange={handleInputChange}
                        required
                        style={{ marginRight: "10px" }}
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1)}{" "}
                      {getTypeIcon(type)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Title Input */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Title (e.g., Customer Name) *
                </label>
                <input
                  type="text"
                  name="ttitle"
                  value={formData.ttitle}
                  onChange={handleInputChange}
                  placeholder="Enter testimonial title or customer name"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Subtitle Input */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Subtitle (e.g., Position/Project)
                </label>
                <input
                  type="text"
                  name="tsub"
                  value={formData.tsub}
                  onChange={handleInputChange}
                  placeholder="Enter customer position or project name"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Detail/Content Textarea */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Testimonial Content
                </label>
                <textarea
                  name="tdet"
                  value={formData.tdet}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Enter the full testimonial text or description"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    resize: "vertical",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Media Upload Section (Conditional Rendering) */}
              {renderMediaUploadSection()}

              {/* Live Status Checkbox */}
              <div style={{ marginBottom: "30px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_live"
                    checked={!!formData.is_live}
                    onChange={handleInputChange}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "10px",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ fontWeight: "600", color: "#374151" }}>
                    Publish Live
                  </span>
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "0.9rem",
                      marginLeft: "10px",
                    }}
                  >
                    (Check this to make the testimonial visible on the website)
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={uploading}
                style={{
                  background: uploading
                    ? "#ccc"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "10px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: uploading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  if (!uploading) e.target.style.opacity = "0.9";
                }}
                onMouseOut={(e) => {
                  if (!uploading) e.target.style.opacity = "1";
                }}
              >
                {uploading ? (
                  <>
                    <FiUpload
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    Saving...
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  </>
                ) : (
                  <>
                    <FiEdit />
                    {editingTestimonial
                      ? "Update Testimonial"
                      : "Save Testimonial"}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* View Testimonial Modal */}
      {showViewModal && viewingTestimonial && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "30px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "25px",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "20px",
              }}
            >
              <h2 style={{ fontSize: "2rem", color: "#1e293b", margin: "0" }}>
                Testimonial Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#64748b",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.color = "#ef4444";
                }}
                onMouseOut={(e) => {
                  e.target.style.color = "#64748b";
                }}
              >
                <FiX />
              </button>
            </div>

            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
              }}
            >
              {/* Status and Type Badges */}
              <span
                style={{
                  ...getStatusColor(viewingTestimonial.is_live),
                  padding: "8px 15px",
                  borderRadius: "10px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {viewingTestimonial.is_live ? <FiEye /> : <FiEyeOff />}
                {viewingTestimonial.is_live ? "Live" : "Draft"}
              </span>
              <span
                style={{
                  ...getTypeColor(viewingTestimonial.ttype),
                  padding: "8px 15px",
                  borderRadius: "10px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {getTypeIcon(viewingTestimonial.ttype)}
                {viewingTestimonial.ttype.charAt(0).toUpperCase() +
                  viewingTestimonial.ttype.slice(1)}
              </span>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "1.5rem",
                  color: "#1e293b",
                  marginBottom: "5px",
                }}
              >
                {viewingTestimonial.ttitle}
              </h3>
              <p
                style={{
                  fontSize: "1.1rem",
                  color: "#64748b",
                  margin: "0 0 15px 0",
                }}
              >
                {viewingTestimonial.tsub || "No Subtitle"}
              </p>
              <p
                style={{
                  fontSize: "1rem",
                  color: "#374151",
                  lineHeight: "1.6",
                  background: "#f8fafc",
                  padding: "15px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #667eea",
                }}
              >
                <FaQuoteLeft
                  style={{ marginRight: "10px", color: "#667eea" }}
                />
                {viewingTestimonial.tdet || "No Content Provided"}
              </p>
            </div>

            {/* Media Display */}
            <div
              style={{
                marginTop: "30px",
                padding: "20px",
                background: "#f1f5f9",
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <h4
                style={{
                  color: "#1e293b",
                  marginBottom: "15px",
                  borderBottom: "1px dashed #cbd5e1",
                  paddingBottom: "10px",
                }}
              >
                {viewingTestimonial.ttype.toUpperCase()} CONTENT
              </h4>

              {viewingTestimonial.ttype === "image" &&
                viewingTestimonial.timg && (
                  <img
                    src={IMaeURl + viewingTestimonial.timg}
                    alt="Testimonial Media"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                    }}
                  />
                )}

              {viewingTestimonial.ttype === "video" && (
                <>
                  {viewingTestimonial.tvlink ? (
                    <div style={{ maxWidth: "100%", margin: "0 auto" }}>
                      <a
                        href={viewingTestimonial.tvlink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "10px",
                          color: "#667eea",
                          textDecoration: "none",
                          fontWeight: "600",
                          fontSize: "1.1rem",
                          padding: "10px",
                          borderRadius: "8px",
                          background: "white",
                          border: "1px solid #667eea",
                        }}
                      >
                        <FiLink /> View Video Link
                      </a>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "0.9rem",
                          marginTop: "10px",
                        }}
                      >
                        {viewingTestimonial.tvlink}
                      </p>
                    </div>
                  ) : viewingTestimonial.timg ? (
                    <video
                      src={IMaeURl + viewingTestimonial.timg}
                      controls
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                      }}
                    />
                  ) : (
                    <p style={{ color: "#94a3b8" }}>
                      No video link or file uploaded.
                    </p>
                  )}
                </>
              )}

              {viewingTestimonial.ttype === "text" && (
                <p style={{ color: "#94a3b8" }}>
                  This is a pure text testimonial, content is shown above.
                </p>
              )}
            </div>

            <div
              style={{
                marginTop: "30px",
                borderTop: "1px solid #e2e8f0",
                paddingTop: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.9rem",
                color: "#64748b",
              }}
            >
              <p style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <FiCalendar /> Created: {formatDate(viewingTestimonial.cr_date)}
              </p>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewingTestimonial);
                }}
                style={{
                  background: "#667eea",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.opacity = "0.9";
                }}
                onMouseOut={(e) => {
                  e.target.style.opacity = "1";
                }}
              >
                <FiEdit /> Quick Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;
